<template>
  <div
    class="vacancies-page container-wrapper"
    :class="{ 'vacancies-page--sent': isSent }"
  >
    <v-container v-if="!isSent" class="pa-0">
      <v-row>
        <v-col class="text-center mb-4 mb-lg-5">
          <h1>Open Roles in AI, Development & SEO</h1>
          <p class="vacancies-page__subtitle">
            Build high-impact products with ARI.company. We are hiring across modern AI engineering,
            product development and performance-led SEO operations.
          </p>
        </v-col>
      </v-row>

      <v-row class="mb-6 mb-lg-8">
        <v-col cols="12">
          <div class="vacancies-page__overview">
            <div class="vacancies-page__overview_item">
              <span>{{ totalVacancies }}</span>
              <p>Open positions</p>
            </div>
            <div class="vacancies-page__overview_item">
              <span>3</span>
              <p>Core departments</p>
            </div>
            <div class="vacancies-page__overview_item">
              <span>Remote</span>
              <p>Work format</p>
            </div>
            <div class="vacancies-page__overview_item">
              <span>Trainee to Head</span>
              <p>Seniority levels</p>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mb-6 mb-lg-8">
        <v-col
          v-for="group in vacancyGroups"
          :key="group.key"
          cols="12"
          class="mb-6"
        >
          <section class="vacancies-group">
            <div class="vacancies-group__head">
              <h2>{{ group.title }}</h2>
              <div class="vacancies-group__count">{{ group.roles.length }} open roles</div>
            </div>
            <div class="vacancies-group__grid">
              <article
                v-for="(role, index) in group.roles"
                :key="role.id"
                class="vacancy-card"
                :class="{ 'vacancy-card--hot': index < 2 || role.isHot }"
              >
                <span v-if="index < 2 || role.isHot" class="vacancy-card__hot" aria-label="Hot vacancy">
                  <v-icon small color="#ff8a00">local_fire_department</v-icon>
                </span>
                <div class="vacancy-card__head">
                  <h3>{{ role.title }}</h3>
                  <span class="vacancy-card__level">{{ role.level }}</span>
                </div>
                <p class="vacancy-card__meta">Full-time · Remote · Global</p>
                <p class="vacancy-card__summary">{{ role.summary }}</p>
                <div class="vacancy-card__tags">
                  <span v-for="skill in role.skills" :key="`${role.id}-${skill}`">{{ skill }}</span>
                </div>
                <button class="vacancy-card__apply" @click="selectVacancy(role.title)">
                  Apply for this role
                </button>
              </article>
            </div>
          </section>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="text-center mb-4">
          <h2 class="vacancies-page__apply_title">Apply With Your CV</h2>
          <p class="vacancies-page__apply_subtitle">
            Upload your CV in PDF, DOC or DOCX format. Maximum file size: 1 MB.
          </p>
          <p v-if="selectedVacancy" class="vacancies-page__selected_role">
            Selected role: <strong>{{ selectedVacancy }}</strong>
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex justify-center">
          <div id="vacancy-upload" class="custom-file-input">
            <label
                for="upload-photo"
                class="custom-file-input__label"
            >
              <span
                  v-if="!uploadFile"
                  class="custom-file-input__label_upload"
              >
                <img src="@/assets/icons/upload.svg" alt="Upload">
                <span>Drag and drop here</span>
              </span>
              <span v-else>
                {{ uploadFile.name }}
              </span>
            </label>
            <input
                id="upload-photo"
                class="custom-file-input__button"
                type="file"
                accept="application/pdf, .doc, .docx"
                @change="handleFileChange"
            >
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="text-center mt-4 mt-lg-5">
          <v-alert
              v-if="recaptchaError"
              type="error"
              dense
          >
            {{ recaptchaError }}
          </v-alert>
          <button
              class="green-btn vacancies-page__button px-8"
              :disabled="!uploadFile"
              @click="sendDataToDB"
          >
            Apply
          </button>
        </v-col>
      </v-row>
    </v-container>
    <div v-else class="text-center">
      <h1 class="mb-4">Thank you!</h1>
      <div>You’ve successfully applied for the job.</div>
      <div>We’ll get back to you shortly.</div>
    </div>

    <v-snackbar
        v-model="fileSnackbar"
        :timeout="fileTimeout"
        color="error"
    >
      The file size should not exceed 1 MB.

      <template v-slot:action="{ attrs }">
        <v-btn
            dark
            text
            v-bind="attrs"
            @click="fileSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {
  appendRecaptchaKeySlot,
  recaptchaConfigurationError
} from '@/config/recaptcha'

export default {
  name: "VacanciesPage",
  data () {
    return {
      uploadFile: null,
      selectedVacancy: '',
      isSent: false,
      rcaptchaHash: '',
      recaptchaError: '',

      fileSnackbar: false,
      fileTimeout: 2000,
      vacancyGroups: [
        {
          key: 'ai',
          title: 'AI Engineering',
          roles: [
            {
              id: 'ai-1',
              title: 'Senior LLM Engineer',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Design, optimise and productionise LLM pipelines for managed client delivery at scale.',
              skills: ['LLMOps', 'Python', 'Prompt Engineering', 'Evaluation']
            },
            {
              id: 'ai-2',
              title: 'AI Product Engineer',
              level: 'Middle / Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe / UK',
              summary: 'Bridge business goals and AI workflows, turning product requirements into reliable delivery systems.',
              skills: ['Product Thinking', 'Python', 'Automation', 'Analytics']
            },
            {
              id: 'ai-3',
              title: 'MLOps & AI Infrastructure Engineer',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'EU Timezones',
              summary: 'Build robust training, deployment and monitoring infrastructure for AI-assisted content operations.',
              skills: ['MLOps', 'Docker', 'CI/CD', 'Observability']
            },
            {
              id: 'ai-4',
              title: 'AI Automation Engineer',
              level: 'Middle',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Implement reliable automation flows that connect data, content generation and publishing layers.',
              skills: ['Workflow Automation', 'Python', 'APIs', 'Integrations']
            },
            {
              id: 'ai-5',
              title: 'AI Quality & Evaluation Specialist',
              level: 'Middle',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Define quality frameworks, run output validation and improve AI reliability across client workflows.',
              skills: ['Quality Control', 'Evaluation', 'Benchmarking', 'Documentation']
            }
          ]
        },
        {
          key: 'development',
          title: 'Software Development',
          roles: [
            {
              id: 'dev-1',
              title: 'Senior Full-Stack Engineer (Vue + Node.js)',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Develop and scale internal platforms that power managed AI, delivery operations and reporting.',
              skills: ['Vue', 'Node.js', 'TypeScript', 'System Design']
            },
            {
              id: 'dev-2',
              title: 'Backend Engineer (Python / FastAPI)',
              level: 'Middle / Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe / LATAM',
              summary: 'Build high-throughput APIs, service integrations and data pipelines for operational products.',
              skills: ['Python', 'FastAPI', 'PostgreSQL', 'Queues']
            },
            {
              id: 'dev-3',
              title: 'Frontend Engineer (Vue.js)',
              level: 'Middle',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'EU Timezones',
              summary: 'Create high-quality internal interfaces focused on clarity, speed and operational usability.',
              skills: ['Vue 2', 'SCSS', 'UX Precision', 'Component Design']
            },
            {
              id: 'dev-4',
              title: 'DevOps / Platform Engineer',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Own platform reliability, deployment automation and secure infrastructure for critical workflows.',
              skills: ['Kubernetes', 'AWS/GCP', 'Terraform', 'Security']
            },
            {
              id: 'dev-5',
              title: 'QA Automation Engineer',
              level: 'Middle',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Design automated testing layers that protect release quality across AI and product systems.',
              skills: ['Automation Testing', 'Playwright', 'API Testing', 'CI']
            }
          ]
        },
        {
          key: 'seo',
          title: 'SEO & Growth Operations',
          roles: [
            {
              id: 'seo-1',
              title: 'Head of SEO',
              level: 'Head',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Own the SEO strategy, lead planning across markets and drive predictable organic growth performance.',
              skills: ['SEO Leadership', 'Strategy', 'Forecasting', 'Cross-team Management']
            },
            {
              id: 'seo-2',
              title: 'SEO Team Lead',
              level: 'Lead',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe / UK',
              summary: 'Manage day-to-day delivery, mentor specialists and align technical, content and off-page execution.',
              skills: ['Team Leadership', 'Execution Planning', 'SEO Operations', 'Performance Management']
            },
            {
              id: 'seo-3',
              title: 'Senior SEO Specialist',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Execute technical and on-page initiatives, improve rankings and scale high-value organic traffic.',
              skills: ['Technical SEO', 'On-page SEO', 'SERP Analysis', 'Content Optimisation']
            },
            {
              id: 'seo-4',
              title: 'Junior SEO Specialist',
              level: 'Junior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Support SEO delivery through keyword research, content updates, QA checks and reporting tasks.',
              skills: ['Keyword Research', 'Content QA', 'Basic Technical SEO', 'Reporting']
            },
            {
              id: 'seo-5',
              title: 'SEO Trainee',
              level: 'Trainee',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Europe',
              summary: 'Learn core SEO workflows by assisting the team with audits, content tasks and platform updates.',
              skills: ['SEO Fundamentals', 'Content Basics', 'Attention to Detail', 'Analytical Mindset']
            },
            {
              id: 'seo-6',
              title: 'Head of Outreach / Linkbuilding',
              isHot: true,
              level: 'Head',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Lead authority growth via strategic outreach, partnerships and scalable link acquisition programmes.',
              skills: ['Outreach Strategy', 'Link Building', 'Digital PR', 'Partnership Development']
            },
            {
              id: 'seo-7',
              title: 'SEO AI Manager',
              isHot: true,
              level: 'Senior / Lead',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Own AI-powered SEO workflows, align model-assisted production with ranking goals, and drive scalable execution.',
              skills: ['AI SEO', 'Workflow Design', 'Prompt Strategy', 'Performance Ownership']
            },
            {
              id: 'seo-8',
              title: 'Programmatic SEO & Automation Specialist',
              isHot: true,
              level: 'Middle / Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Build programmatic landing systems and automation logic to scale indexable, high-intent SEO assets.',
              skills: ['Programmatic SEO', 'Automation', 'Data Structuring', 'Template Logic']
            },
            {
              id: 'seo-9',
              title: 'LLM SEO Content Operations Manager',
              level: 'Senior',
              type: 'Full-time',
              workModel: 'Remote',
              location: 'Global',
              summary: 'Run AI-assisted content operations, control quality at scale, and coordinate publication across SEO pipelines.',
              skills: ['LLM Operations', 'Editorial QA', 'Content Systems', 'SEO Execution']
            }
          ]
        }
      ]
    }
  },
  computed: {
    totalVacancies () {
      return this.vacancyGroups.reduce((total, group) => total + group.roles.length, 0)
    }
  },
  methods: {
    selectVacancy (title) {
      this.selectedVacancy = title

      const uploadSection = document.getElementById('vacancy-upload')
      if (uploadSection) {
        uploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    async recaptcha () {
      this.recaptchaError = recaptchaConfigurationError
      this.rcaptchaHash = ''
      if (this.recaptchaError) return

      try {
        await this.$recaptchaLoaded()
        this.rcaptchaHash = await this.$recaptcha('job')
        if (!this.rcaptchaHash) {
          this.recaptchaError = 'Form verification failed. Please try again.'
        }
      } catch (error) {
        this.recaptchaError = 'Form verification failed. Please try again.'
      }
    },
    handleFileChange (e) {
      let file = e.target.files[0]
      if (file && file.size  > 1000000) {
        this.fileSnackbar = true
        document.getElementById('upload-photo').value = ''
      } else if (file) {
        this.uploadFile = e.target.files[0]
      }
    },
    async postData (formData) {
      const res = await fetch('https://api.ari.company/api/form/jobs', {
        method: 'POST',
        body: formData
      });
      return await res.json();
    },
    async sendDataToDB () {
      this.recaptchaError = recaptchaConfigurationError
      if (this.recaptchaError) return
      await this.recaptcha()
      if (!this.rcaptchaHash) return

      if (this.uploadFile && this.rcaptchaHash !== '') {
        const formData = new FormData()
        const selectedRole = this.selectedVacancy && this.selectedVacancy.trim()
          ? this.selectedVacancy.trim()
          : 'General Application'

        formData.append('file', this.uploadFile)
        formData.append('rcaptcha', this.rcaptchaHash)
        appendRecaptchaKeySlot(formData)
        formData.append('vacancy', selectedRole)
        formData.append('subject', `Vacancy Application: ${selectedRole}`)

        this.postData(formData)
          .then(data => {
            if (data.status === 'OK') {
              this.isSent = true
            }
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vacancies-page {
  flex: 1 0 auto;
  padding-top: 120px;
  padding-bottom: 160px;

  h1 {
    color: #223750;
    margin-bottom: 12px;
  }

  &__subtitle {
    max-width: 820px;
    margin: 0 auto;
    color: #657990;
    font-size: 16px;
    line-height: 1.75;
  }

  &__overview {
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;
  }

  &__overview_item {
    border: 1px solid #d9e4f0;
    border-radius: 12px;
    background: #f7fbff;
    padding: 16px 18px;
    text-align: center;

    span {
      display: block;
      color: #223750;
      font-weight: 700;
      font-size: 22px;
      line-height: 1.2;
      margin-bottom: 4px;
    }

    p {
      margin: 0;
      color: #71859d;
      font-size: 13px;
    }
  }

  &__apply_title {
    color: #223750;
    margin-bottom: 8px;
  }

  &__apply_subtitle {
    margin: 0;
    color: #71859d;
  }

  &__selected_role {
    margin: 10px 0 0;
    color: #2d4f74;
  }

  .custom-file-input {
    width: 260px;
    height: 120px;

      &__label_upload span {
          width: 100%;
          margin-top: 5px;
      }
  }

  &__button {
    width: 100%;
  }

  &.vacancies-page--sent {
    display: flex;
    justify-content: center;
    align-items: center;

    div {
      font-weight: 400;
      font-size: 14px;
    }
  }

  @media (min-width: $mobile-small) {
    &__overview {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__button {
      width: 230px;
    }
  }

  @media (min-width: $mobile-small) {
    h1 {
      font-size: 46px;
    }
  }

  @media (min-width: $pad-medium) {
    padding-top: 140px;
    padding-bottom: 220px;

    &__overview {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .custom-file-input {
      width: 320px;
      height: 150px;
    }
  }
}

.vacancies-group {
  border: 1px solid #d7e3f0;
  border-radius: 16px;
  background: #ffffff;
  padding: 18px;
  box-shadow: 0 10px 26px rgba(17, 42, 72, 0.06);
}

.vacancies-group__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 26px;
    color: #223750;
  }
}

.vacancies-group__count {
  border: 1px solid #d6e2ef;
  border-radius: 999px;
  padding: 6px 12px;
  color: #546983;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.vacancies-group__grid {
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
}

.vacancy-card {
  position: relative;
  border: 1px solid #dce7f2;
  border-radius: 14px;
  background: #fbfdff;
  padding: 16px;
  min-height: 248px;
  display: flex;
  flex-direction: column;
}

.vacancy-card--hot {
  border-color: #ffb857;
  box-shadow: 0 14px 32px rgba(252, 160, 27, 0.18);
  background: linear-gradient(180deg, #fffdfa 0%, #fbfdff 100%);

  .vacancy-card__head {
    padding-left: 48px;
  }
}

.vacancy-card__hot {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  border-radius: 999px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ffc36a;
  background: linear-gradient(180deg, #ffe4b7 0%, #ffd28a 100%);
}

.vacancy-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 19px;
    line-height: 1.35;
    color: #233850;
  }
}

.vacancy-card__level {
  border: 1px solid #c7daef;
  border-radius: 999px;
  padding: 5px 10px;
  color: #2f5f8b;
  background: #edf6ff;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.vacancy-card__meta {
  margin: 0 0 10px;
  color: #60758d;
  font-size: 13px;
}

.vacancy-card__summary {
  margin: 0 0 12px;
  color: #4f657d;
  font-size: 14px;
  line-height: 1.65;
}

.vacancy-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;

  span {
    border-radius: 999px;
    border: 1px solid #d7e4f1;
    background: #ffffff;
    padding: 4px 8px;
    font-size: 11px;
    color: #4d647c;
  }
}

.vacancy-card__apply {
  margin-top: auto;
  border: 1px solid #8bb6e2;
  border-radius: 999px;
  padding: 9px 14px;
  color: #1e517f;
  background: #ecf6ff;
  font-weight: 600;
  transition: 0.2s ease;

  &:hover {
    background: #dff0ff;
    border-color: #6ea8dc;
  }
}

@media (min-width: $mobile-large) {
  .vacancies-group__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: $desk-small) {
  .vacancies-group__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
